import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

import esCommon from './locales/es/common.json';
import esLanding from './locales/es/landing.json';
import esAuth from './locales/es/auth.json';
import esDashboard from './locales/es/dashboard.json';
import esCreateTrip from './locales/es/createTrip.json';
import esTrip from './locales/es/trip.json';

/**
 * TOVÍAS i18n.
 *
 * Scope (intentionally narrow): landing, auth (login/register), dashboard
 * shell and the Create Trip dialog. Everything else stays in Spanish until
 * we get real demand for additional surfaces.
 *
 * Spanish ('es') is the default — the user base is Spanish-first and we want
 * fresh visitors with `Accept-Language: en-*` to see English without any
 * setup, while keeping ES as the persistent fallback (e.g. for ES users
 * roaming abroad whose browser temporarily reports en-US).
 *
 * PERF (Feb 2026): English translations are dynamically imported the first
 * time i18next asks for them. Ships ~30 KB less JS to the ~99 % of visitors
 * who never switch language. The Spanish bundle is still eager so the
 * landing renders with correct copy from the first paint.
 */
const resources = {
  es: {
    common: esCommon,
    landing: esLanding,
    auth: esAuth,
    dashboard: esDashboard,
    createTrip: esCreateTrip,
    trip: esTrip,
  },
};

// Lazy-load English bundles on demand. i18next calls back into `read` when
// it needs a namespace that isn't in `resources` yet.
const lazyEnBackend = {
  type: 'backend',
  init() {},
  read: async (lng, ns, callback) => {
    if (lng !== 'en') return callback(new Error('unsupported'), null);
    try {
      const mod = await import(
        /* webpackChunkName: "i18n-en" */ `./locales/en/${ns}.json`
      );
      callback(null, mod.default || mod);
    } catch (e) {
      callback(e, null);
    }
  },
};

i18n
  .use(lazyEnBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    partialBundledLanguages: true,
    fallbackLng: 'es',
    supportedLngs: ['es', 'en'],
    // Only fall back to language without region (e.g. 'en-US' → 'en').
    load: 'languageOnly',
    ns: ['common', 'landing', 'auth', 'dashboard', 'createTrip', 'trip'],
    defaultNS: 'common',
    detection: {
      // Strict: only honor an explicit choice from localStorage. Don't sniff
      // the browser locale — product wants Spanish as the visible default
      // for fresh visitors so the brand voice stays consistent.
      order: ['localStorage'],
      caches: ['localStorage'],
      lookupLocalStorage: 'tovias_lang',
    },
    interpolation: { escapeValue: false }, // React already escapes
    returnNull: false,
  });

export default i18n;
